import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Save, Send, Server, Link2, CheckCircle, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const { user } = useAuth();
  const [smtp, setSmtp] = useState({ server: "mail.infomaniak.com", port: 587, sender_email: "", password: "", notification_email: "" });
  const [loomy, setLoomy] = useState({ api_key: "" });
  const [loomyTest, setLoomyTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [savingLoomy, setSavingLoomy] = useState(false);
  const [testingLoomy, setTestingLoomy] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- init-only fetch on mount
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/settings/smtp`, { withCredentials: true }).catch(() => ({ data: {} })),
      axios.get(`${API}/admin/settings/loomy`, { withCredentials: true }).catch(() => ({ data: {} })),
    ]).then(([smtpRes, loomyRes]) => {
      const s = smtpRes.data;
      setSmtp(prev => ({ ...prev, server: s.server || prev.server, port: s.port || prev.port, sender_email: s.sender_email || "", notification_email: s.notification_email || "" }));
      setLoomy({ api_key: loomyRes.data.api_key || "" });
      setLoading(false);
    });
  }, []);

  const handleSaveSmtp = async () => {
    setSavingSmtp(true);
    try {
      await axios.post(`${API}/admin/settings/smtp`, smtp, { withCredentials: true });
      toast.success("Configuration SMTP sauvegardée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de sauvegarde");
    } finally { setSavingSmtp(false); }
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    try {
      const { data } = await axios.post(`${API}/admin/settings/smtp/test`, {}, { withCredentials: true });
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec du test SMTP");
    } finally { setTestingSmtp(false); }
  };

  const handleSaveLoomy = async () => {
    setSavingLoomy(true);
    try {
      await axios.post(`${API}/admin/settings/loomy`, { api_key: loomy.api_key }, { withCredentials: true });
      toast.success("Configuration Loomy sauvegardée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de sauvegarde");
    } finally { setSavingLoomy(false); }
  };

  const handleTestLoomy = async () => {
    setTestingLoomy(true);
    setLoomyTest(null);
    try {
      const { data } = await axios.post(`${API}/admin/settings/loomy/test`, {}, { withCredentials: true });
      setLoomyTest(data);
      if (data.success) toast.success(`Connexion OK — ${data.brand_name}`);
      else toast.error("Clé API invalide");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec du test Loomy");
      setLoomyTest({ success: false });
    } finally { setTestingLoomy(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#336DBF]" /></div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">Paramètres</h1>
        <p className="text-sm text-slate-500 mt-1">Configuration de l'application</p>
      </div>

      {/* Loomy CRM */}
      <Card className="border-slate-200">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <Link2 className="w-5 h-5 text-[#336DBF]" />
            <h2 className="text-lg font-semibold text-slate-900">Loomy CRM</h2>
          </div>
          <p className="text-sm text-slate-500">Clé API pour l'envoi automatique des leads vers Loomy CRM.</p>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Clé API / Brand Key</label>
            <input value={loomy.api_key} onChange={e => setLoomy({ api_key: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#336DBF]"
              placeholder="brand_xxxxxxxx" data-testid="loomy-api-key" />
          </div>

          {loomyTest && (
            <div className={`flex items-center gap-2 text-sm ${loomyTest.success ? "text-emerald-600" : "text-red-600"}`}>
              {loomyTest.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {loomyTest.success ? `Connecté — ${loomyTest.brand_name} (${loomyTest.status})` : "Clé API invalide"}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button onClick={handleSaveLoomy} disabled={savingLoomy} className="gap-2 bg-[#336DBF] hover:bg-[#2A5A9E]" data-testid="loomy-save">
              {savingLoomy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Sauvegarder
            </Button>
            <Button onClick={handleTestLoomy} disabled={testingLoomy} variant="outline" className="gap-2" data-testid="loomy-test">
              {testingLoomy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              Tester la connexion
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* SMTP */}
      <Card className="border-slate-200">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <Server className="w-5 h-5 text-[#336DBF]" />
            <h2 className="text-lg font-semibold text-slate-900">Configuration SMTP</h2>
          </div>
          <p className="text-sm text-slate-500">Configurez l'envoi d'emails pour les notifications de leads et confirmations clients.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Serveur SMTP</label>
              <input value={smtp.server} onChange={e => setSmtp({...smtp, server: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#336DBF]"
                placeholder="mail.infomaniak.com" data-testid="smtp-server" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Port</label>
              <input type="number" value={smtp.port} onChange={e => setSmtp({...smtp, port: parseInt(e.target.value) || 587})}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#336DBF]"
                data-testid="smtp-port" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email expéditeur</label>
              <input type="email" value={smtp.sender_email} onChange={e => setSmtp({...smtp, sender_email: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#336DBF]"
                placeholder="noreply@nettoyage-expert.ch" data-testid="smtp-sender" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mot de passe SMTP</label>
              <input type="password" value={smtp.password} onChange={e => setSmtp({...smtp, password: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#336DBF]"
                placeholder="••••••••" data-testid="smtp-password" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Email destinataire des notifications</label>
              <input type="email" value={smtp.notification_email} onChange={e => setSmtp({...smtp, notification_email: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#336DBF]"
                placeholder="admin@nettoyage-expert.ch" data-testid="smtp-notification" />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button onClick={handleSaveSmtp} disabled={savingSmtp} className="gap-2 bg-[#336DBF] hover:bg-[#2A5A9E]" data-testid="smtp-save">
              {savingSmtp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Sauvegarder
            </Button>
            <Button onClick={handleTestSmtp} disabled={testingSmtp} variant="outline" className="gap-2" data-testid="smtp-test">
              {testingSmtp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Tester la connexion
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Infos entreprise */}
      <Card className="border-slate-200">
        <CardContent className="p-6 space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">Informations entreprise</h2>
          <div className="text-sm text-slate-600 space-y-1">
            <p><strong>Raison sociale :</strong> Nettoyage Expert</p>
            <p><strong>Adresse :</strong> Rue Jean-André Venel 4, 1400 Yverdon-les-Bains</p>
            <p><strong>Téléphone :</strong> 024 539 10 33</p>
            <p><strong>Email :</strong> info@nettoyage-expert.ch</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
